import React, { useState, useMemo, useEffect, useCallback, useRef, Suspense } from 'react';
import { Event } from '../../../types/event.types';
import { EventSeries } from '../../../types/eventSeries.types';
import RecurringEventDetails from './RecurringEventDetails';
import EventCardKPIs from './EventCardKPIs';
import EventCardMenu from './EventCardMenu';
import ConfidenceBadge from '../EventImport/ConfidenceBadge';
import PublishChecklistModal from './PublishChecklistModal';
import PublishLimitModal from './PublishLimitModal';
import OccurrenceEditModal from './OccurrenceEditModal';
import AddOccurrenceModal from './AddOccurrenceModal';
import { locationCache } from '../../../services/LocationCacheService';
import {
  StatusMessage,
  Icon,
  Button
} from '../../../pages/DesignSystem';
import { formatEventDateTime, formatEventTime } from '../../../utils/timezoneUtils';
import { useOrganizerEventsStore } from '../../../stores/organizerEventsStore';
import { useEventActions } from '../../../hooks/useEventActions';
import { useOrganizer } from '../../../contexts/OrganizerContext';
import { optimizeCloudinaryUrl } from '../../../services/cloudinary.service';
import './EventList.css';

// Lazy-loaded Instagram Post Generator (zero impact on main bundle)
const InstaPostGenerator = React.lazy(() =>
  import('../InstaPostGenerator').then((m) => ({ default: m.InstaPostGenerator }))
);

// Empty state messages for Active tab (randomly selected)
const EMPTY_STATE_MESSAGES = [
  {
    title: "Ready to shine?",
    subtitle: "Create your first event and let the world discover it!"
  },
  {
    title: "Your stage is waiting!",
    subtitle: "Time to create an amazing event for your audience."
  },
  {
    title: "No events yet?",
    subtitle: "Let's fix that! Create your first event in seconds."
  },
  {
    title: "The spotlight is yours!",
    subtitle: "Share your next event with thousands of potential attendees."
  },
  {
    title: "Keep the momentum going!",
    subtitle: "Your audience is ready for your next event."
  },
  {
    title: "What's next on your agenda?",
    subtitle: "Create an event and let people know what you're planning!"
  }
];

interface EventListProps {
  events: Event[];
  activeTab?: 'active' | 'draft' | 'past';  // Pour filtrage intelligent
  onEventSelect: (event: Event, modifyType?: 'single' | 'series' | 'occurrence') => void;
  onEventStatusChange?: (eventId: string, newStatus: 'draft' | 'active') => void;
  onUnpublishOccurrence?: (event: Event) => void;
  onDuplicate?: (duplicatedEvent: Partial<Event>) => void;  // Duplication côté client
  // Props pour le scroll infini
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => Promise<void>;
  // Desktop: ID de l'event sélectionné pour le highlight
  selectedEventId?: string;
  // Metrics
  metricsLoading?: boolean;
  // Desktop: Two column masonry layout
  twoColumnLayout?: boolean;
  // Empty state CTA for Active tab
  onCreateEvent?: () => void;
}

interface GroupedEvents {
  single: Event[];
  recurring: {
    [seriesId: string]: {
      mainEvent: Event;
      events: Event[];
    };
  };
}

const EventList: React.FC<EventListProps> = ({
  events,
  activeTab,
  onEventSelect,
  onEventStatusChange,
  onDuplicate,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  metricsLoading = false,
  twoColumnLayout = false,
  onCreateEvent
}) => {
  const [expandedSeries, setExpandedSeries] = useState<string[]>([]);
  const [showStatusMessage, setShowStatusMessage] = useState<{ id: number; text: string } | null>(null);
  const [statusError, setStatusError] = useState<{ id: number; text: string } | null>(null);
  const messageIdRef = useRef(0);

  // Random empty state message for Active tab (selected once on mount)
  const [emptyStateMessage] = useState(() =>
    EMPTY_STATE_MESSAGES[Math.floor(Math.random() * EMPTY_STATE_MESSAGES.length)]
  );

  // Publish checklist modal state
  const [showPublishChecklist, setShowPublishChecklist] = useState(false);
  const [publishChecklistEvent, setPublishChecklistEvent] = useState<Event | null>(null);
  const [missingFields, setMissingFields] = useState<string[]>([]);

  // Publish limit modal state (pay-per-event)
  const [showPublishLimit, setShowPublishLimit] = useState(false);
  const [publishLimitEvent, setPublishLimitEvent] = useState<Event | null>(null);
  const [publishLimitQuota, setPublishLimitQuota] = useState<{ used: number; limit: number }>({ used: 0, limit: 5 });

  // Occurrence edit modal state (simplified date/time editing)
  const [showOccurrenceEdit, setShowOccurrenceEdit] = useState(false);
  const [occurrenceEditEvent, setOccurrenceEditEvent] = useState<Event | null>(null);

  // Add occurrence modal state (creates independent event from series template)
  const [showAddOccurrence, setShowAddOccurrence] = useState(false);
  const [addOccurrenceSeries, setAddOccurrenceSeries] = useState<EventSeries | null>(null);

  // Instagram Post Generator state
  const [instaEvent, setInstaEvent] = useState<Event | null>(null);

  // Organizer context for organizerId
  const { organizerProfile } = useOrganizer();

  // Metrics store
  const eventMetrics = useOrganizerEventsStore(state => state.eventMetrics);

  // Event actions hook (unpublish only)
  const handleActionSuccess = useCallback((message: { type: 'success' | 'error'; text: string }) => {
    const newId = ++messageIdRef.current;
    if (message.type === 'success') {
      setShowStatusMessage({ id: newId, text: message.text });
      setTimeout(() => setShowStatusMessage(null), 3000);
    } else {
      setStatusError({ id: newId, text: message.text });
      setTimeout(() => setStatusError(null), 3000);
    }
  }, []);

  const { unpublishEvent, publishEvent, deleteEvent, cancelEvent, restoreEvent, cancelSeries, restoreSeries } = useEventActions(handleActionSuccess);

  // Duplication côté client - copie tout sauf le schedule
  const handleDuplicate = useCallback((event: Event) => {
    if (!onDuplicate) return;

    const duplicatedEvent: Partial<Event> = {
      title: event.title,
      description: event.description,
      location: event.location,
      locationName: event.locationName,
      latitude: event.latitude,
      longitude: event.longitude,
      geohash: event.geohash,
      tags: [...(event.tags || [])],
      image: event.image,
      prices: event.prices ? [...event.prices] : [],
      ticketingUrl: event.ticketingUrl,
      organizerContact: event.organizerContact,
      timezone: event.timezone, // 🌍 Preserve timezone when duplicating
      status: 'draft' as const,
    };

    onDuplicate(duplicatedEvent);
  }, [onDuplicate]);

  const getEventDate = (event: Event): Date | null => {
    return event.datetime || null;
  };

  // Required fields for publication
  const REQUIRED_FIELDS_FOR_PUBLISH = [
    { key: 'title', label: 'Title' },
    { key: 'datetime', label: 'Start Date' },
    { key: 'location', label: 'Location' },
    { key: 'description', label: 'Description' }
  ];

  const getValidationResult = (event: Event): { isValid: boolean; missingFields: string[] } => {
    const missing: string[] = [];

    REQUIRED_FIELDS_FOR_PUBLISH.forEach(field => {
      const value = event[field.key as keyof Event];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        missing.push(field.label);
      }
    });

    return {
      isValid: missing.length === 0,
      missingFields: missing
    };
  };

  const validateEventForPublication = (event: Event): boolean => {
    const validation = getValidationResult(event);

    if (!validation.isValid) {
      // Show checklist modal with missing fields
      setMissingFields(validation.missingFields);
      setPublishChecklistEvent(event);
      setShowPublishChecklist(true);
      return false;
    }
    return true;
  };

  /**
   * Handle publishing an event with quota checking
   * Shows PublishLimitModal if user has reached their event limit
   */
  const handlePublish = async (event: Event) => {
    // First validate required fields
    if (!validateEventForPublication(event)) {
      return;
    }

    // Call publish with Stripe RPC (server-side quota checking)
    const result = await publishEvent(event.id, event.organizerId);

    // If limit reached, show payment modal
    if (result.limitReached) {
      setPublishLimitEvent(event);
      setPublishLimitQuota({
        used: result.eventsUsed || 0,
        limit: result.eventsLimit || 5
      });
      setShowPublishLimit(true);
    }

    // Success or other errors are handled by the hook's onSuccess callback
  };

  const handleClosePublishChecklist = () => {
    setShowPublishChecklist(false);
    setPublishChecklistEvent(null);
    setMissingFields([]);
  };

  const handleEditFromChecklist = () => {
    if (publishChecklistEvent) {
      onEventSelect(publishChecklistEvent);
    }
    handleClosePublishChecklist();
  };

  const handleClosePublishLimit = () => {
    setShowPublishLimit(false);
    setPublishLimitEvent(null);
    setPublishLimitQuota({ used: 0, limit: 5 });
  };

  const handleStatusChange = (event: Event, newStatus: 'draft' | 'active') => {
    if (!onEventStatusChange) return;

    setStatusError(null);

    if (newStatus === 'active' && !validateEventForPublication(event)) {
      return;
    }

    onEventStatusChange(event.id, newStatus);
    const message = newStatus === 'draft'
      ? "Event has been removed from the public platform"
      : "Event is now visible on the public platform";

    const newId = ++messageIdRef.current;
    setShowStatusMessage({ id: newId, text: message });
    setTimeout(() => setShowStatusMessage(null), 3000);
  };

  const groupedEvents = useMemo(() => {
    const grouped: GroupedEvents = {
      single: [],
      recurring: {}
    };

    events.forEach(event => {
      if (event.seriesId) {
        if (!grouped.recurring[event.seriesId]) {
          grouped.recurring[event.seriesId] = {
            mainEvent: event,
            events: []
          };
        }
        grouped.recurring[event.seriesId].events.push(event);
        
        const currentMainDate = getEventDate(grouped.recurring[event.seriesId].mainEvent);
        const newDate = getEventDate(event);
        if (currentMainDate && newDate && newDate < currentMainDate) {
          grouped.recurring[event.seriesId].mainEvent = event;
        }
      } else {
        grouped.single.push(event);
      }
    });

    return grouped;
  }, [events]);

  // ✅ TRI INTELLIGENT PAR SÉRIE - Filtrage après groupement
  // Note: Cancelled events appear in the 'active' tab (they were published)
  const filteredSeries = useMemo(() => {
    if (!activeTab) return groupedEvents; // Si pas d'onglet spécifié, tout afficher

    const now = new Date();
    const filtered = {
      single: groupedEvents.single.filter(event => {
        switch (activeTab) {
          case 'active':
            // Include active AND cancelled events (cancelled events stay in Active tab)
            return (event.status === 'active' || event.status === 'cancelled') &&
                   event.datetime && new Date(event.datetime) >= now;
          case 'draft':
            return event.status === 'draft';
          case 'past':
            return (event.status === 'active' || event.status === 'cancelled') &&
                   event.datetime && new Date(event.datetime) < now;
          default:
            return true;
        }
      }),
      recurring: {} as typeof groupedEvents.recurring
    };

    // Filtrage intelligent des séries récurrentes
    Object.entries(groupedEvents.recurring).forEach(([seriesId, seriesData]) => {
      const { mainEvent, events: seriesEvents } = seriesData;

      if (activeTab === 'draft') {
        // Pour les brouillons, inclure seulement les séries en draft
        if (mainEvent.status === 'draft') {
          filtered.recurring[seriesId] = seriesData;
        }
      } else {
        // Pour active/past, vérifier si la série a des occurrences dans cette catégorie
        // Include cancelled events in active tab
        const hasActiveOccurrences = seriesEvents.some(event =>
          (event.status === 'active' || event.status === 'cancelled') &&
          event.datetime && new Date(event.datetime) >= now
        );
        const hasPastOccurrences = seriesEvents.some(event =>
          (event.status === 'active' || event.status === 'cancelled') &&
          event.datetime && new Date(event.datetime) < now
        );

        if ((activeTab === 'active' && hasActiveOccurrences) ||
            (activeTab === 'past' && hasPastOccurrences)) {
          // ✅ CORRECTION CRITIQUE : Filtrer les occurrences affichées selon l'onglet
          const filteredOccurrences = seriesEvents.filter(event => {
            if (activeTab === 'active') {
              return (event.status === 'active' || event.status === 'cancelled') &&
                     event.datetime && new Date(event.datetime) >= now;
            } else { // past
              return (event.status === 'active' || event.status === 'cancelled') &&
                     event.datetime && new Date(event.datetime) < now;
            }
          });

          filtered.recurring[seriesId] = {
            ...seriesData,
            events: filteredOccurrences
          };
        }
      }
    });

    return filtered;
  }, [groupedEvents, activeTab]);

  // Unified items array for two-column masonry layout
  type ListItem =
    | { type: 'recurring'; seriesId: string; group: { mainEvent: Event; events: Event[] } }
    | { type: 'single'; event: Event };

  const allItems = useMemo((): ListItem[] => {
    const items: ListItem[] = [];

    // Add recurring events
    Object.entries(filteredSeries.recurring).forEach(([seriesId, group]) => {
      items.push({ type: 'recurring', seriesId, group });
    });

    // Add single events
    filteredSeries.single.forEach(event => {
      items.push({ type: 'single', event });
    });

    // Sort mixed list by reference date:
    // - Single events: use datetime
    // - Recurring events (Active tab): use NEXT upcoming occurrence (>= now)
    // - Recurring events (other tabs): use LAST occurrence datetime
    const now = new Date();
    items.sort((a, b) => {
      const getRefDate = (item: ListItem): Date | null => {
        if (item.type === 'single') {
          return item.event.datetime ? new Date(item.event.datetime) : null;
        } else {
          const dates = item.group.events
            .map(e => e.datetime ? new Date(e.datetime) : null)
            .filter((d): d is Date => d !== null);
          if (dates.length === 0) return null;

          if (activeTab === 'active') {
            // Sort by next upcoming occurrence so the series appears
            // where it logically "happens next" in the timeline
            const upcoming = dates.filter(d => d >= now).sort((a, b) => a.getTime() - b.getTime());
            return upcoming.length > 0 ? upcoming[0] : dates[dates.length - 1];
          }
          // Past/Draft: use last occurrence
          return new Date(Math.max(...dates.map(d => d.getTime())));
        }
      };

      const dateA = getRefDate(a);
      const dateB = getRefDate(b);

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      // Past tab: most recent first; others: chronological order
      return activeTab === 'past'
        ? dateB.getTime() - dateA.getTime()
        : dateA.getTime() - dateB.getTime();
    });

    return items;
  }, [filteredSeries, activeTab]);

  const handleEditSeries = (event: Event) => {
    // ✅ CORRECTION : Utiliser les données originales pour l'édition (toutes les occurrences)
    const seriesEvents = event.seriesId ? groupedEvents.recurring[event.seriesId].events : [];
    const enrichedEvent = {
      ...event,
      isRecurring: true,
      seriesEvents
    };
    onEventSelect(enrichedEvent, 'series');
  };

  // ✨ Opens the simplified occurrence edit modal (date/time only)
  const handleEditOccurrence = (event: Event) => {
    setOccurrenceEditEvent(event);
    setShowOccurrenceEdit(true);
  };

  const handleCloseOccurrenceEdit = () => {
    setShowOccurrenceEdit(false);
    setOccurrenceEditEvent(null);
  };

  // ✨ Opens the add occurrence modal (creates independent event from series)
  const handleAddOccurrence = async (event: Event) => {
    if (!event.seriesId) return;

    try {
      // Get series data from cache
      const seriesData = await locationCache.getSeries(event.seriesId);
      if (seriesData) {
        setAddOccurrenceSeries(seriesData);
        setShowAddOccurrence(true);
      }
    } catch (error) {
      console.error('Error loading series data:', error);
    }
  };

  const handleCloseAddOccurrence = () => {
    setShowAddOccurrence(false);
    setAddOccurrenceSeries(null);
  };

  // Helper function to render a recurring card
  const renderRecurringCard = (seriesId: string, group: { mainEvent: Event; events: Event[] }) => (
    <div
      key={seriesId}
      className={`organizer-card organizer-card--recurring ${group.mainEvent.status === 'cancelled' ? 'organizer-card--cancelled' : ''}`}
      onClick={(event) => {
        const target = event?.target as HTMLElement;
        if (target.closest('.organizer-card__actions')) return;

        const currentExpanded = expandedSeries.includes(seriesId);
        setExpandedSeries(currentExpanded
          ? expandedSeries.filter(id => id !== seriesId)
          : [...expandedSeries, seriesId]
        );
      }}
    >
      <RecurringEventDetails
        mainEvent={group.mainEvent}
        seriesEvents={group.events}
        activeTab={activeTab}
        onEditSeries={handleEditSeries}
        onEditOccurrence={handleEditOccurrence}
        onStatusChange={handleStatusChange}
        onCancelOccurrence={(event) => cancelEvent(event.id)}
        onRestoreOccurrence={(event) => restoreEvent(event.id)}
        onDuplicateSeries={(event) => handleDuplicate(event)}
        onCancelSeries={(event) => event.seriesId && cancelSeries(event.seriesId)}
        onRestoreSeries={(event) => event.seriesId && restoreSeries(event.seriesId)}
        onPublishSeries={(event) => handlePublish(event)}
        onDeleteSeries={(event) => deleteEvent(event.id)}
        onAddOccurrence={handleAddOccurrence}
        onInstagram={(event) => setInstaEvent(event)}
        isExpanded={expandedSeries.includes(seriesId)}
        onToggleExpand={(expanded) => {
          setExpandedSeries(expanded
            ? [...expandedSeries, seriesId]
            : expandedSeries.filter(id => id !== seriesId)
          );
        }}
      />
    </div>
  );

  // Helper function to render a single event card
  const renderSingleCard = (event: Event) => (
    <div
      key={event.id}
      className={`organizer-card ${event.status === 'cancelled' ? 'organizer-card--cancelled' : ''}`}
      onClick={() => onEventSelect(event)}
    >
      <div className="organizer-card__header">
        {event.image && (
          <div className="organizer-card__image">
            <img
              src={optimizeCloudinaryUrl(event.image, { width: 200 })}
              alt={event.title || 'Untitled'}
              className="organizer-card__image-content"
            />
          </div>
        )}
        <div className="organizer-card__content">
          <div className="organizer-card__content-top">
            <h3 className="organizer-card__title">
              {event.status === 'cancelled' && (
                <span className="organizer-card__cancelled-badge">CANCELLED</span>
              )}
              <span className="organizer-card__title-text">{event.title || 'Untitled'}</span>
            </h3>
            <div className="organizer-card__actions" onClick={(e) => e.stopPropagation()}>
              {/* Green Publish button for drafts */}
              {event.status === 'draft' && activeTab === 'draft' && (
                <Button
                  variant="primary"
                  size="sm"
                  className="organizer-card__publish-btn"
                  onClick={() => handlePublish(event)}
                >
                  <Icon name="Send" size={14} />
                  Publish
                </Button>
              )}
              {event.status === 'active' && activeTab === 'active' && (
                <button
                  className="organizer-card__insta-btn"
                  onClick={() => setInstaEvent(event)}
                  title="Generate Instagram post"
                >
                  <Icon name="Instagram" size={18} />
                </button>
              )}
              <EventCardMenu
                eventId={event.id}
                eventStatus={event.status as 'active' | 'draft' | 'cancelled'}
                activeTab={activeTab}
                onEdit={() => onEventSelect(event)}
                onDuplicate={() => handleDuplicate(event)}
                onUnpublish={() => unpublishEvent(event.id, event.organizerId)}
                onPublish={() => handlePublish(event)}
                onDelete={() => deleteEvent(event.id)}
                onCancel={() => cancelEvent(event.id)}
                onRestore={() => restoreEvent(event.id)}
                onInstagram={() => setInstaEvent(event)}
              />
            </div>
          </div>
          <p className="organizer-card__date">
            {event.status === 'draft' ? (
              'Draft'
            ) : (
              <>
                <span>{formatEventDateTime(event.datetime, event.timezone).date}</span>
                <span className="organizer-card__time-range">
                  {formatEventDateTime(event.datetime, event.timezone).time}
                  {event.endTime && ` - ${formatEventTime(event.endTime, event.timezone)}`}
                </span>
              </>
            )}
          </p>
          <p className="organizer-card__location">
            {event.locationName || event.location || 'Location not set'}
          </p>
          <EventCardKPIs
            metrics={eventMetrics[event.id] || null}
            isLoading={metricsLoading}
            variant="compact"
          />
          {statusError && event.status === 'draft' && (
            <div className="organizer-card__error">
              <Icon name="AlertTriangle" size={16} />
              {statusError.text}
            </div>
          )}
        </div>
      </div>
      {event.status === 'draft' && event.importConfidence != null && (
        <div className="organizer-card__confidence">
          <ConfidenceBadge confidence={event.importConfidence} />
        </div>
      )}
    </div>
  );

  // Helper to render an item (recurring or single)
  const renderItem = (item: ListItem) => {
    if (item.type === 'recurring') {
      return renderRecurringCard(item.seriesId, item.group);
    }
    return renderSingleCard(item.event);
  };

  // Effet pour le scroll infini
  useEffect(() => {
    if (!hasMore || !onLoadMore) return;

    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Déclencher le chargement quand on est à 100px du bas
      if (scrollTop + windowHeight >= documentHeight - 100 && !isLoadingMore) {
        onLoadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, isLoadingMore, onLoadMore]);

  return (
    <div className="organizer-list">
      <StatusMessage
        messages={showStatusMessage ? [{
          id: showStatusMessage.id,
          text: showStatusMessage.text,
          type: 'success'
        }] : []}
        onDismiss={() => setShowStatusMessage(null)}
      />
      <StatusMessage
        messages={statusError ? [{
          id: statusError.id,
          text: statusError.text,
          type: 'error'
        }] : []}
        onDismiss={() => setStatusError(null)}
      />

      {/* Empty state for Active tab - rendered at top level for proper centering */}
      {allItems.length === 0 && activeTab === 'active' && onCreateEvent && (
        <div className="organizer-empty-state organizer-empty-state--active">
          <Icon name="Sparkles" size={48} className="organizer-empty-state__icon" />
          <h3 className="organizer-empty-state__title">{emptyStateMessage.title}</h3>
          <p className="organizer-empty-state__subtitle">{emptyStateMessage.subtitle}</p>
          <Button variant="primary" onClick={onCreateEvent} >
            <Icon name="Plus" size={18} />
            Create an Event
          </Button>
        </div>
      )}

      {/* Content or other empty states */}
      {twoColumnLayout ? (
        /* Two-column masonry layout */
        <div className="organizer-list__columns">
          <div className="organizer-list__column">
            {allItems.filter((_, i) => i % 2 === 0).map(renderItem)}
          </div>
          <div className="organizer-list__column">
            {allItems.filter((_, i) => i % 2 === 1).map(renderItem)}
          </div>
          {allItems.length === 0 && !(activeTab === 'active' && onCreateEvent) && (
            <div className="organizer-card organizer-card--empty organizer-list__empty-full">
              <Icon name="Calendar" size={24} />
              <p>No events in this category</p>
            </div>
          )}
        </div>
      ) : (
        /* Single column layout (mobile/default) */
        <div className="organizer-list__container">
          {allItems.map(renderItem)}
          {allItems.length === 0 && !(activeTab === 'active' && onCreateEvent) && (
            <div className="organizer-card organizer-card--empty">
              <Icon name="Calendar" size={24} />
              <p>No events in this category</p>
            </div>
          )}
          {isLoadingMore && (
            <div className="organizer-card organizer-card--loading">
              <div className="organizer-card__loading-content">
                <Icon name="Loader" size={20} />
                <p>Loading more events...</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Publish Checklist Modal */}
      <PublishChecklistModal
        isOpen={showPublishChecklist}
        onClose={handleClosePublishChecklist}
        missingFields={missingFields}
        onEditClick={handleEditFromChecklist}
      />

      {/* Publish Limit Modal (pay-per-event) */}
      {publishLimitEvent && organizerProfile && (
        <PublishLimitModal
          isOpen={showPublishLimit}
          onClose={handleClosePublishLimit}
          eventId={publishLimitEvent.id}
          organizerId={organizerProfile.id}
          eventsUsed={publishLimitQuota.used}
          eventsLimit={publishLimitQuota.limit}
        />
      )}

      {/* Occurrence Edit Modal (simplified date/time editing) */}
      <OccurrenceEditModal
        isOpen={showOccurrenceEdit}
        onClose={handleCloseOccurrenceEdit}
        event={occurrenceEditEvent}
        onSuccess={handleCloseOccurrenceEdit}
      />

      {/* Add Occurrence Modal (creates independent event from series template) */}
      <AddOccurrenceModal
        isOpen={showAddOccurrence}
        onClose={handleCloseAddOccurrence}
        series={addOccurrenceSeries}
        onSuccess={handleCloseAddOccurrence}
      />

      {/* Instagram Post Generator (lazy-loaded) */}
      {instaEvent && (
        <Suspense fallback={null}>
          <InstaPostGenerator
            isOpen={!!instaEvent}
            onClose={() => setInstaEvent(null)}
            event={instaEvent}
            organizerName={organizerProfile?.organizer}
            organizerProfile={organizerProfile}
          />
        </Suspense>
      )}
    </div>
  );
};

export default EventList;
